import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { recipesAPI, adminAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import StarRating from './StarRating';
import './RecipeDetail.css';

export default function RecipeDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [recipe, setRecipe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [currentImage, setCurrentImage] = useState(0);
  const [isFaved, setIsFaved] = useState(false);
  const [rating, setRating] = useState({ average: 0, count: 0 });
  const [myScore, setMyScore] = useState(null);
  const [regenerating, setRegenerating] = useState(false);

  useEffect(() => {
    recipesAPI.getOne(id)
      .then(data => {
        setRecipe(data.recipe);
        setIsFaved(data.recipe.addedBy?.includes(user?._id));
        setCurrentImage(data.recipe.mainImageId || 0);
      })
      .catch(() => navigate('/'))
      .finally(() => setLoading(false));
    recipesAPI.getRating(id).then(setRating).catch(() => {});
    if (user) {
      recipesAPI.getMyRating(id).then(data => setMyScore(data.score)).catch(() => {});
    }
  }, [id, navigate, user?._id]);

  const handleRate = async (score) => {
    try {
      const data = await recipesAPI.rateRecipe(id, score);
      setRating({ average: data.average, count: data.count });
      setMyScore(data.myScore);
    } catch { /* ignore */ }
  };

  const handleComment = async (e) => {
    e.preventDefault();
    if (!comment.trim() || comment.length > 45) return;
    setSubmitting(true);
    try {
      const data = await recipesAPI.addComment(id, comment.trim());
      setRecipe(prev => ({ ...prev, comments: data.comments }));
      setComment('');
    } catch { /* ignore */ }
    setSubmitting(false);
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await recipesAPI.deleteComment(id, commentId);
      setRecipe(prev => ({
        ...prev,
        comments: prev.comments.filter(c => c._id !== commentId),
      }));
    } catch { /* ignore */ }
  };

  const handleFavorite = async () => {
    try {
      const data = await recipesAPI.toggleFavorite(id);
      setIsFaved(data.added);
    } catch { /* ignore */ }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this recipe?')) return;
    try {
      await recipesAPI.delete(id);
      navigate('/');
    } catch { /* ignore */ }
  };

  const handleRegeneratePhoto = async () => {
    if (!window.confirm('Regenerate this recipe\'s photo from Unsplash?')) return;
    setRegenerating(true);
    try {
      const data = await adminAPI.regeneratePhoto(id);
      setRecipe(prev => ({ ...prev, images: data.images }));
      setCurrentImage(0);
    } catch (err) {
      alert(err.message || 'Failed to regenerate photo');
    }
    setRegenerating(false);
  };

  if (loading) return <div className="loading-center"><div className="spinner" /></div>;
  if (!recipe) return null;

  const isOwner = user?._id === recipe.createdBy?._id;
  const isAdmin = user?.role === 'admin';
  const creator = recipe.createdBy;

  return (
    <div className="recipe-detail fade-in">
      {/* Image Gallery */}
      {recipe.images?.length > 0 && (
        <div className="rd-gallery">
          <img
            src={recipe.images[currentImage]?.imageURL}
            alt={recipe.name}
            className="rd-main-image"
          />
          {recipe.images.length > 1 && (
            <div className="rd-thumbnails">
              {recipe.images.map((img, i) => (
                <img
                  key={i}
                  src={img.imageURL}
                  alt={`${recipe.name} ${i + 1}`}
                  className={`rd-thumb ${i === currentImage ? 'active' : ''}`}
                  onClick={() => setCurrentImage(i)}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Header */}
      <div className="rd-header card">
        <div className="rd-header-top">
          <div>
            <h1 className="rd-title">{recipe.name}</h1>
            <p className="rd-description">{recipe.description}</p>
          </div>
          <div className="rd-actions">
            {user && (
              <button className={`btn btn-sm ${isFaved ? 'btn-primary' : 'btn-outline'}`} onClick={handleFavorite}>
                {isFaved ? '♥' : '♡'} <span className="rd-btn-label">{isFaved ? 'Saved' : 'Save'}</span>
              </button>
            )}
            {(isOwner || isAdmin) && (
              <>
                <Link to={`/edit-recipe/${recipe._id}`} className="btn btn-secondary btn-sm">✏️ <span className="rd-btn-label">Edit</span></Link>
                {isAdmin && (
                  <button
                    className="btn btn-outline btn-sm"
                    onClick={handleRegeneratePhoto}
                    disabled={regenerating}
                  >
                    {regenerating ? '⏳' : '🔄'} <span className="rd-btn-label">{regenerating ? 'Regenerating...' : 'Regen Photo'}</span>
                  </button>
                )}
                <button className="btn btn-danger btn-sm" onClick={handleDelete}>🗑 <span className="rd-btn-label">Delete</span></button>
              </>
            )}
          </div>
        </div>

        {/* Creator */}
        {creator && (
          <Link to={`/profile/${creator._id}`} className="rd-creator">
            <div className="user-avatar-md">
              {creator.profilePicture ? (
                <img src={creator.profilePicture} alt={creator.username} />
              ) : creator.username?.[0]?.toUpperCase()}
            </div>
            <div>
              <div className="rd-creator-name">{creator.username}</div>
              <div className="rd-creator-date">
                {new Date(recipe.creationDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
              </div>
            </div>
          </Link>
        )}

        {/* Tags */}
        <div className="rd-tags">
          {recipe.categories?.map(c => <span key={c} className="badge badge-orange">{c}</span>)}
          {recipe.classifications?.map(c => <span key={c} className="badge badge-green">{c}</span>)}
          {recipe.avoids?.map(c => <span key={c} className="badge badge-red">No {c}</span>)}
          {!recipe.isPublic && <span className="badge badge-red">Private</span>}
        </div>

        {/* Rating */}
        <StarRating
          average={rating.average}
          count={rating.count}
          myScore={myScore}
          onRate={handleRate}
          canRate={!!user}
        />
      </div>

      {/* Recipe Content */}
      <div className="rd-content card">
        <h2>Recipe</h2>
        <div className="rd-html" dangerouslySetInnerHTML={{ __html: recipe.recipeHtml }} />
        {recipe.notes && (
          <div className="rd-notes">
            <h3>Notes</h3>
            <p>{recipe.notes}</p>
          </div>
        )}
      </div>

      {/* Comments */}
      <div className="rd-comments card">
        <h2>Comments ({recipe.comments?.length || 0})</h2>
        {user ? (
          <form onSubmit={handleComment} className="rd-comment-form">
            <input
              className="input-field"
              placeholder="Add a comment (max 45 chars)..."
              value={comment}
              onChange={(e) => setComment(e.target.value.slice(0, 45))}
              maxLength={45}
            />
            <button type="submit" className="btn btn-primary btn-sm" disabled={submitting || !comment.trim()}>Post</button>
          </form>
        ) : (
          <p style={{ color: 'var(--gray)', fontSize: 13, marginBottom: 12 }}>
            <Link to="/login" style={{ color: 'var(--orange)', fontWeight: 600 }}>Log in</Link> to leave a comment
          </p>
        )}

        <div className="rd-comment-list">
          {recipe.comments?.slice().reverse().map(c => (
            <div key={c._id} className="rd-comment">
              <div className="rd-comment-header">
                <strong>{c.username}</strong>
                <span className="rd-comment-date">
                  {new Date(c.createdAt).toLocaleDateString()}
                </span>
                {(c.user === user?._id || c.user?._id === user?._id || isAdmin) && (
                  <button className="rd-comment-delete" onClick={() => handleDeleteComment(c._id)}>×</button>
                )}
              </div>
              <p>{c.message}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
